import { AdsAccount, AdsGroupAd } from '@betterfunnel/types';
import * as Sentry from '@sentry/react';
import { httpsCallable } from 'firebase/functions';
import {
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { functions } from '../Firebase';
import { useDebounce } from '@betterfunnel/react-hooks';
import { WithId } from '../utils';
import { UserContext } from './UserContext';

export type AdsAccountProvider = AdsAccount['provider'];

interface AdsAccountContextProps {
  loadingAds: boolean;
  ads?: WithId<AdsGroupAd>[];
  loadMoreAds: () => void;
  query?: string;
  setQuery: (q?: string) => void;
  filter?: any;
  setFilter: (f?: any) => void;
  generateCampaignFromAd: (adId: string) => Promise<any>;
  resyncAdsAccounts: () => Promise<any>;
  deleteAdsAccounts: (
    provider: AdsAccountProvider,
    providerids?: string[],
  ) => Promise<any>;
}

export const AdsAccountContext = createContext<AdsAccountContextProps>({
  loadingAds: false,
  loadMoreAds: () => {},
  generateCampaignFromAd: async () => '',
  resyncAdsAccounts: async () => '',
  deleteAdsAccounts: async () => '',
  setQuery: () => {},
  setFilter: () => {},
});

const fetchAds = async (
  provider: string,
  accountId: string,
  query?: string,
  filter?: any,
  skip?: number,
) => {
  let _query = query?.trim();
  const _filter = { ...filter };

  if (_query && _query.length >= 10 && _query.length <= 12 && !isNaN(+_query)) {
    _filter.$or = [
      { adGroupExternalId: _query },
      { campaignExternalId: _query },
    ];
    _query = '';
  }

  const fn = httpsCallable(functions, 'accounts-adsAccounts-getAdsAccountsAds');

  const res = await fn({
    accountId,
    provider,
    query: _query,
    filter: _filter,
    limit: 100,
    skip: skip || 0,
  });

  return (res.data as any).map((a: any) => ({ ...a, id: a.externalId }));
};

export function AdsAccountContextProvider({
  provider,
  children,
}: {
  provider: AdsGroupAd['provider'];
  children: ReactElement<any>;
}) {
  const { selectedAccount, refetchAccounts } = useContext(UserContext);

  const [query, setQuery] = useState<string>();
  const [filter, setFilter] = useState<any>({ status: 'enabled' });
  const [ads, setAds] = useState<WithId<AdsGroupAd>[]>();
  const [loadingAds, setLoadingAds] = useState(false);

  const debouncedQuery = useDebounce(query, 500);
  const debouncedFilter = useDebounce(filter, 500);

  useEffect(() => {
    if (!selectedAccount) {
      setAds(undefined);
      return;
    }

    setLoadingAds(true);
    setAds(undefined);

    const accountId = selectedAccount.id;
    let relevant = true;

    fetchAds(provider, accountId, debouncedQuery, debouncedFilter)
      .then((res) => {
        if (!relevant) return;
        setAds(res as any);
      })
      .catch((e) => {
        console.error(e);
        Sentry.captureException(e);
      })
      .finally(() => {
        setLoadingAds(false);
      });

    return () => {
      relevant = false;
    };
  }, [selectedAccount, provider, debouncedQuery, debouncedFilter]);

  useEffect(() => {
    if (ads?.length) return;

    const interval = setInterval(() => {
      setFilter((f: any) => ({ ...f }));
    }, 10000);

    return () => {
      clearInterval(interval);
    };
  }, [selectedAccount, provider, ads]);

  const generateCampaignFromAd = useCallback(
    (adId: string) => {
      const fn = httpsCallable(
        functions,
        'accounts-campaigns-createCampaignFromAd',
      );

      return fn({
        accountId: selectedAccount?.id,
        adId,
      }).then((res) => {
        return res.data as string;
      });
    },
    [selectedAccount],
  );

  const resyncAdsAccounts = useCallback(() => {
    const fn = httpsCallable(functions, 'accounts-adsAccounts-resync');

    return fn({
      accountId: selectedAccount?.id,
    }).then((res) => {
      return res.data as string;
    });
  }, [selectedAccount]);

  const deleteAdsAccounts = useCallback(
    (provider: AdsAccountProvider, ids?: string[]) => {
      const fn = httpsCallable(functions, 'accounts-adsAccounts-disconnect');

      return fn({
        accountId: selectedAccount?.id,
        provider,
        adsAccountsIds: ids,
      }).then(async (res) => {
        await refetchAccounts();
        return res.data as string;
      });
    },
    [selectedAccount?.id, refetchAccounts],
  );

  const loadMoreAds = useCallback(() => {
    if (!selectedAccount) return;

    setLoadingAds(true);
    const accountId = selectedAccount.id;
    fetchAds(provider, accountId, query, filter, ads?.length)
      .then((res) => {
        setAds((ads) => [...(ads || []), ...(res as any)]);
      })
      .catch((e) => {
        console.error(e);
        Sentry.captureException(e);
      })
      .finally(() => {
        setLoadingAds(false);
      });
  }, [selectedAccount, provider, query, filter, ads]);

  return (
    <AdsAccountContext.Provider
      value={{
        loadingAds,
        ads,
        loadMoreAds,
        query,
        setQuery,
        filter,
        setFilter,
        generateCampaignFromAd,
        resyncAdsAccounts,
        deleteAdsAccounts,
      }}
    >
      {children}
    </AdsAccountContext.Provider>
  );
}
